import { ApplicationInsights, SeverityLevel } from '@microsoft/applicationinsights-web';
import { ErrorHandler, Injectable } from '@angular/core';
import { environment } from "../../environments/environment";
import * as Sentry from '@sentry/angular';

export interface SentryLogOptions {
  extras?: Record<string, any>;
  context?: { name: string; data: Record<string, unknown> };
  tags?: Record<string, string>;
  severityLevel?: Sentry.SeverityLevel;
}

export interface ExceptionLogOptions {
  properties?: { [key: string]: any };
  sentry?: SentryLogOptions;
  skipSentry?: boolean;
}

@Injectable({ providedIn: 'root' })
export class LoggingService {
  appInsights: ApplicationInsights;
  constructor() {
    this.appInsights = new ApplicationInsights({
      config: {
        instrumentationKey: environment.appInsights.instrumentationKey,
        enableAutoRouteTracking: true,
        disableFetchTracking: false,
        disableDataLossAnalysis: false,
      }
    });
    this.appInsights.loadAppInsights();
  }

  logPageView(name?: string, url?: string) { 
    this.appInsights.trackPageView({
      name: name,
      uri: url
    });
  }

  logEvent(name: string, properties?: { [key: string]: any }) {
    this.appInsights.trackEvent({ name: name}, properties);
  }

  logMetric(name: string, average: number, properties?: { [key: string]: any }) {
    this.appInsights.trackMetric({ name: name, average: average }, properties);
  }

  logException(exception: Error, severityLevel?: number, options?: { [key: string]: any } | ExceptionLogOptions) {
    const normalizedOptions = this.normalizeExceptionLogOptions(options);
    const normalizedException = this.normalizeThrowable(exception);
    const mergedProperties = {
      ...(normalizedOptions.properties ?? {}),
      ...this.extractErrorProperties(exception),
    };

    this.appInsights.trackException(
      { exception: normalizedException, severityLevel: severityLevel },
      mergedProperties
    );

    if (!normalizedOptions.skipSentry) {
      this.logExceptionSentry(exception, normalizedOptions.sentry, mergedProperties);
    }
  }

  logTrace(message: string, properties?: { [key: string]: any }) {
    this.appInsights.trackTrace({ message: message}, properties);
  }

  logExceptionSentry(error: any, sentryOptions?: SentryLogOptions, exceptionProperties?: { [key: string]: any })
  {
    const normalizedError = this.normalizeThrowable(error);

    const id = Sentry.withScope(scope => {
      if (sentryOptions?.context?.name && sentryOptions.context.data) {
        scope.setContext(sentryOptions.context.name, sentryOptions.context.data);
      }

      if (sentryOptions?.tags) {
        scope.setTags(sentryOptions.tags);
      }

      if (sentryOptions?.extras) {
        scope.setExtras(sentryOptions.extras);
      }

      if (exceptionProperties) {
        scope.setExtras({
          ...(sentryOptions?.extras ?? {}),
          ...exceptionProperties,
        });
      }

      if (sentryOptions?.severityLevel) {
        scope.setLevel(sentryOptions.severityLevel);
      }

      return Sentry.captureException(normalizedError);
    });

    this.appInsights.trackException(
      { exception: normalizedError, severityLevel: undefined },
      {
        ...(exceptionProperties ?? {}),
        "Sentry Trace ID": id,
      }
    );
  }

  private normalizeExceptionLogOptions(
    options?: { [key: string]: any } | ExceptionLogOptions
  ): ExceptionLogOptions {
    if (!options) {
      return {};
    }

    if ('properties' in options || 'sentry' in options || 'skipSentry' in options) {
      return options as ExceptionLogOptions;
    }

    return { properties: options };
  }

  private normalizeThrowable(error: any): Error {
    if (error instanceof Error) {
      return error;
    }

    if (error?.message && typeof error.message === 'string') {
      const typedError = new Error(error.message);
      typedError.name = error.name || 'HandledError';
      if (error.stack) {
        typedError.stack = error.stack;
      }
      return typedError;
    }

    return new Error(this.safeSerialize(error));
  }

  private extractErrorProperties(error: any): { [key: string]: any } {
    if (!error || error instanceof Error) {
      return {};
    }

    const properties: { [key: string]: any } = {};

    if (typeof error.status !== 'undefined') {
      properties.status = error.status;
    }

    if (typeof error.statusText !== 'undefined') {
      properties.statusText = error.statusText;
    }

    if (typeof error.url !== 'undefined') {
      properties.url = error.url;
    }

    if (typeof error.name !== 'undefined') {
      properties.errorName = error.name;
    }

    if (typeof error.message !== 'undefined') {
      properties.errorMessage = error.message;
    }

    if (typeof error.error !== 'undefined') {
      properties.errorBody = this.safeSerialize(error.error);
    }

    return properties;
  }

  private safeSerialize(value: any): string {
    if (typeof value === 'string') {
      return value;
    }

    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }
}

@Injectable()
export class ErrorHandlerService extends ErrorHandler {

  constructor(private loggingService: LoggingService) {
      super();
  }

  handleError(error: any) {
    const id = Sentry.captureException(error instanceof Error ? error : new Error(error));

    this.loggingService.logException(error, undefined, {
      properties: { "Sentry Trace ID": id },
      skipSentry: true,
    });
  }

    
}